// Product card colour dots (snippets/gf-card-swatches.liquid; feature branch feature/card-colour-swatches, 2026-10-14).
// Each dot is a link to the product page on its colour, so it works without JavaScript. Here a plain tap or click keeps
// the shopper on the page and switches the card to that colour instead: its own photo (or the "Photo of this option
// coming soon" panel, never another colour's photo) laid inside the frame of the card's image and built like it, the
// card's links (?variant=), the image source label, and the price when the card shows one variant's price (a price range
// stays). Choosing the card's first colour again removes the layer and puts back the links and label as rendered, so the
// card is exactly as it was. A click with a modifier key opens the link as usual.
// One listener on document in the capture phase (before the card's own navigation handler), with an init guard, so cards
// re-rendered by filters, pagination, recommendations or the theme editor need nothing.
if (!window.gfCardSwatchesInit) {
  window.gfCardSwatchesInit = true;

  const LINKS = 'a.product-card__link, a[ref="cardGalleryLink"], a[ref="productTitleLink"]';

  /**
   * The card as rendered, read before its first switch: its links' href attributes and the label under the image
   * (snippets/gf-media-origin.liquid).
   * @type {WeakMap<HTMLElement, { hrefs: Map<HTMLAnchorElement, string | null>, label: { text: string, hidden: boolean } | null }>}
   */
  const rendered = new WeakMap();

  /**
   * The frame of the image the card shows: its visible media container (the current slide), or the "Photo coming
   * soon" panel.
   * @param {Element} gallery
   */
  const frameOf = (gallery) => {
    const frames = [...gallery.querySelectorAll('.product-media-container, .gf-card-nophoto')].filter(
      (el) => !el.closest('.gf-swatch-view') && el.getClientRects().length > 0
    );
    return frames.find((el) => el.getAttribute('aria-hidden') === 'false') ?? frames[0] ?? null;
  };

  /**
   * A dot's photo built like the card's own image (snippets/card-gallery.liquid, snippets/product-media.liquid): the same
   * container classes and fit, the same image class, sizes and loading, so the card media styles size it identically.
   * @param {HTMLAnchorElement} dot
   * @param {Element} frame
   * @param {HTMLElement} card
   */
  const photoOf = (dot, frame, card) => {
    const own = frame.querySelector('img.product-media__image');
    const sizes =
      own?.getAttribute('sizes') ??
      card.closest('.shopify-section')?.querySelector('.card-gallery img.product-media__image[sizes]')?.getAttribute('sizes') ??
      '(min-width: 750px) 25vw, 50vw';
    const box = document.createElement('div');
    box.className = frame.classList.contains('product-media-container')
      ? frame.className
      : 'product-media-container media-fit product-media-container--image';
    box.style.setProperty('--product-media-fit', getComputedStyle(frame).getPropertyValue('--product-media-fit').trim() || 'cover');
    const media = document.createElement('div');
    media.className = 'product-media';
    const img = document.createElement('img');
    img.className = own?.className || 'product-media__image';
    img.alt = dot.dataset.gfAlt || '';
    const loading = own?.getAttribute('loading');
    if (loading) img.setAttribute('loading', loading);
    img.sizes = sizes;
    if (dot.dataset.gfSrcset) img.srcset = dot.dataset.gfSrcset;
    img.src = dot.dataset.gfSrc || '';
    media.append(img);
    box.append(media);
    return box;
  };

  /**
   * @param {HTMLElement} card
   * @param {HTMLAnchorElement} dot
   */
  const select = (card, dot) => {
    const group = dot.closest('[data-gf-swatches]');
    if (!group) return;
    for (const other of group.querySelectorAll('[data-gf-swatch]')) {
      if (other === dot) other.setAttribute('aria-current', 'true');
      else other.removeAttribute('aria-current');
    }

    const variantId = dot.dataset.gfVariantId || '';
    const initial = dot.hasAttribute('data-gf-initial');
    const gallery = card.querySelector('.card-gallery');

    let before = rendered.get(card);
    if (!before) {
      const label = gallery?.parentElement?.querySelector(':scope > .gf-media-origin--card') ?? null;
      before = {
        hrefs: new Map([...card.querySelectorAll(LINKS)].map((link) => [/** @type {HTMLAnchorElement} */ (link), link.getAttribute('href')])),
        label: label ? { text: label.textContent || '', hidden: label.hidden } : null,
      };
      rendered.set(card, before);
    }

    // Links: the card's overlay, image and name links. The first colour puts back the rendered hrefs exactly (with or
    // without ?variant=); another colour sets ?variant= and keeps their other parameters (Horizon's product-card).
    if (initial) {
      for (const [link, href] of before.hrefs) {
        if (href === null) link.removeAttribute('href');
        else link.setAttribute('href', href);
      }
    } else if (typeof card.applyVariantToLinks === 'function') {
      card.applyVariantToLinks(variantId);
    } else {
      for (const link of card.querySelectorAll(LINKS)) {
        const url = new URL(/** @type {HTMLAnchorElement} */ (link).href);
        url.searchParams.set('variant', variantId);
        /** @type {HTMLAnchorElement} */ (link).href = url.toString();
      }
    }

    // Image: a layer inside the frame of the card's own image.
    if (gallery) {
      for (const old of gallery.querySelectorAll('.gf-swatch-view')) old.remove();
      const frame = initial ? null : frameOf(gallery);
      if (frame) {
        const view = document.createElement('div');
        view.className = 'gf-swatch-view';
        view.dataset.gfSwatchView = variantId;
        if (dot.dataset.gfSrc) {
          view.append(photoOf(dot, frame, card));
        } else {
          const pending = group.querySelector('template[data-gf-swatch-pending]');
          if (pending instanceof HTMLTemplateElement) view.append(pending.content.cloneNode(true));
        }
        frame.append(view);
      }
    }

    // Image source label: follows the photo shown; the rendered one comes back with the first colour.
    if (gallery) {
      let label = gallery.parentElement?.querySelector(':scope > .gf-media-origin--card') ?? null;
      const text = initial ? (before.label && !before.label.hidden ? before.label.text : '') : dot.dataset.gfOrigin || '';
      if (text && !label) {
        label = document.createElement('p');
        label.className = 'gf-media-origin gf-media-origin--card';
        gallery.after(label);
      }
      if (label) {
        if (text) label.textContent = text;
        label.hidden = !text;
      }
    }

    // Price: only a card showing one variant's price follows the colour (not a range, not a market price).
    const price = card.querySelector('product-price [ref="priceContainer"]');
    if (price && !price.classList.contains('gf-market-price') && !price.querySelector('.gf-price-note')) {
      const amount = price.querySelector('.price bdi');
      if (amount && dot.dataset.gfPrice) amount.textContent = dot.dataset.gfPrice;
      const title = price.querySelector('.gf-price-variant');
      if (title && dot.dataset.gfVariantTitle) title.textContent = dot.dataset.gfVariantTitle;
    }
  };

  document.addEventListener(
    'click',
    (event) => {
      const dot = event.target instanceof Element ? event.target.closest('a[data-gf-swatch]') : null;
      if (!(dot instanceof HTMLAnchorElement)) return;
      if (event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
      const card = dot.closest('product-card');
      if (!(card instanceof HTMLElement)) return;
      event.preventDefault();
      event.stopPropagation();
      select(card, dot);
    },
    true
  );
}
